import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon } from "@chakra-ui/icons";
import {
  IConsignadoProposal,
  OfertaEscolhidaCustomStatus,
} from "../../../models/consignado/proposal.model";
import { BrlCurrencyFormatter } from "../../../utils/BrlCurrencyFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { getOfertaEscolhida } from "@/utils/consignado/ofertaEscolhida";

const moneyOrNull = (value?: number | null): string | null =>
  value != null ? BrlCurrencyFormatter(value) : null;

const rateOrNull = (value?: number | null): string | null =>
  value != null ? `${value.toFixed(2)}%` : null;

/**
 * Trecho interpolado do script. Fonte única para a renderização (negrito ou
 * placeholder cinza) e para o texto plano do botão Copiar.
 */
type ScriptSegment = string | { value: string | null; placeholder: string };

const buildScriptParagraphs = (
  proposal?: IConsignadoProposal,
  oferta?: OfertaEscolhidaCustomStatus | null,
): ScriptSegment[][] => [
  [
    "Eu, ",
    {
      value: proposal?.nome_cliente ?? null,
      placeholder: "[NOME]",
    },
    ", CPF: ",
    {
      value: proposal?.cpf ? cpfFormatter(proposal.cpf) : null,
      placeholder: "[CPF]",
    },
    ", confirmo que estou fazendo o Crédito do Trabalhador.",
  ],
  [
    "Vou receber ",
    {
      value: moneyOrNull(oferta?.releasedAmount),
      placeholder: "[VALOR LIBERADO]",
    },
    " na minha conta.",
  ],
  [
    "Sei que será descontado do meu salário o valor de ",
    {
      value: moneyOrNull(oferta?.installmentValue),
      placeholder: "[VALOR DA PARCELA]",
    },
    " por mês, diretamente pela empresa onde trabalho, por ",
    {
      value:
        oferta?.numberOfInstallments != null
          ? String(oferta.numberOfInstallments)
          : null,
      placeholder: "[Nº DE PARCELAS]",
    },
    " meses (parcelas).",
  ],
  [
    "Também sei que este empréstimo tem juros de ",
    {
      value: rateOrNull(oferta?.monthlyRate),
      placeholder: "[JUROS A.M.]",
    },
    " ao mês.",
  ],
  [
    "Confirmo que autorizei a contratação do seguro no valor de ",
    {
      value: moneyOrNull(oferta?.insuranceValue),
      placeholder: "[VALOR DO SEGURO]",
    },
    ". Esse valor será descontado do dinheiro do empréstimo.",
  ],
  [
    "Entendo que, se a empresa onde trabalho não descontar as parcelas do meu salário por qualquer motivo, eu continuarei responsável por pagar as parcelas do empréstimo, conforme está no contrato.",
  ],
  [
    "Declaro que recebi as informações sobre este empréstimo, tive a chance de fazer perguntas e esclarecer minhas dúvidas, e estou contratando este empréstimo por minha própria vontade.",
  ],
];

const toPlainText = (paragraphs: ScriptSegment[][]): string =>
  paragraphs
    .map((segments) =>
      segments
        .map((s) => (typeof s === "string" ? s : s.value ?? s.placeholder))
        .join(""),
    )
    .join("\n\n");

/**
 * Valor interpolado no script: negrito quando o dado existe; quando ainda não
 * vem do back, mantém o placeholder entre colchetes em cinza, para o operador
 * enxergar o que falta.
 */
function ScriptValue({
  value,
  placeholder,
}: {
  value: string | null;
  placeholder: string;
}) {
  if (value == null) {
    return (
      <Text as="span" color="gray.400" fontStyle="italic">
        {placeholder}
      </Text>
    );
  }
  return (
    <Text as="span" fontWeight="bold">
      {value}
    </Text>
  );
}

interface ScriptContratacaoModalProps {
  proposal: IConsignadoProposal | undefined;
  onClose: () => void;
}

/**
 * Script de ciência da contratação lido/enviado ao cliente na análise
 * bancária, preenchido com os dados da proposta.
 */
export default function ScriptContratacaoModal({
  proposal,
  onClose,
}: ScriptContratacaoModalProps) {
  const toast = useToast();
  const { oferta } = getOfertaEscolhida(proposal?.custom_status);
  const paragraphs = buildScriptParagraphs(proposal, oferta);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(toPlainText(paragraphs));
      toast({
        title: "Script copiado",
        description: "Cole no canal de atendimento para enviar ao cliente",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error copying script:", error);
      toast({
        title: "Erro",
        description: "Erro ao copiar o script",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={!!proposal} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent fontFamily="Lexend Deca" borderRadius="xl">
        {/* Azul info do autocontratacao-ssr (#1E5AA8) */}
        <ModalHeader
          textAlign="center"
          color="#1E5AA8"
          bg="gray.50"
          borderTopRadius="xl"
        >
          Script de contratação
        </ModalHeader>
        <ModalCloseButton color="red.500" />
        <ModalBody color="gray.800" py={4} bg="gray.50">
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="md"
            p={{ base: 4, md: 6 }}
          >
            {paragraphs.map((segments, index) => (
              <Text key={index} mb={index < paragraphs.length - 1 ? 4 : 0}>
                {segments.map((segment, segmentIndex) =>
                  typeof segment === "string" ? (
                    segment
                  ) : (
                    <ScriptValue
                      key={segmentIndex}
                      value={segment.value}
                      placeholder={segment.placeholder}
                    />
                  ),
                )}
              </Text>
            ))}
          </Box>
        </ModalBody>
        <ModalFooter bg="gray.50" borderBottomRadius="xl">
          <Button
            leftIcon={<CopyIcon />}
            colorScheme="gray"
            onClick={handleCopy}
          >
            Copiar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
